// Presentation mapping for the domain lifecycle: badge variants, stage copy,
// and friendly wording for the technical errors the API can surface.

import type { DomainLifecycleStatus, DomainRecordState } from "@pgs-core/types";

type BadgeVariant = "default" | "secondary" | "success" | "warning" | "destructive" | "outline";

export const STATUS_BADGE: Record<DomainLifecycleStatus, { variant: BadgeVariant; label: string }> = {
  provisioning: { variant: "secondary", label: "Provisioning" },
  pending_dns: { variant: "warning", label: "Awaiting DNS" },
  active: { variant: "success", label: "Active" },
  degraded: { variant: "destructive", label: "Degraded" },
};

export const STAGE_COPY: Record<DomainLifecycleStatus, string> = {
  provisioning: "Setting the domain up with Cloudflare — this usually takes under a minute.",
  pending_dns:
    "Add the CNAME records below at the domain's registrar. Once they propagate, verification and the SSL certificate complete automatically — this can take a few minutes to a few hours.",
  active: "Domain connected & SSL active. The site is being served on the custom domain.",
  degraded:
    "This domain was connected but is no longer healthy — its DNS records may have been changed or removed at the registrar. Check the records below, then re-check.",
};

/** Non-technical per-record hint derived from the raw Cloudflare statuses. */
export function recordHint(record: DomainRecordState): string | null {
  if (record.status === "active") return null;
  if (record.verification_errors.length > 0) {
    const first = record.verification_errors[0];
    if (/not found|missing|does not/i.test(first)) return "Record not found — check the CNAME record.";
    return first;
  }
  const validation = record.cf_ssl_status ?? "";
  if (validation && validation !== "active" && record.cf_hostname_status === "pending") {
    return "SSL certificate still pending.";
  }
  return null;
}

/** Soften API/Cloudflare error messages for display under the input. */
export function friendlyDomainError(message: string): string {
  if (/already connected|already mapped|conflict|already exists|409/i.test(message)) {
    return "Conflict detected — that domain is already connected to another client.";
  }
  if (/valid domain|invalid_input/i.test(message)) {
    return "That doesn't look like a valid domain. Enter it like example.com.";
  }
  if (/pages project/i.test(message)) {
    return "The Pages project must be provisioned before a domain can be connected.";
  }
  if (/cloudflare/i.test(message)) {
    return `Cloudflare rejected the request: ${message}`;
  }
  return message;
}

/** "Checked just now" / "Checked 12m ago" from an ISO timestamp. */
export function checkedAgo(iso: string | null): string {
  if (!iso) return "Not checked yet";
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms) || ms < 0) return "Checked just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "Checked just now";
  if (minutes < 60) return `Checked ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Checked ${hours}h ago`;
  return `Checked ${Math.floor(hours / 24)}d ago`;
}
